/**
 * Mock API client
 *
 * Returns mock data when VITE_USE_MOCK_DATA is enabled, so the whole app runs
 * without a backend — for development and for tests.
 *
 * Two layers, checked in this order:
 *
 *   1. `CUSTOM_ROUTES` — exact paths (or regexes) with their own handler, for
 *      everything that is not plain CRUD: login, the current user, the reset
 *      flow, ...
 *   2. `HANDLER_ROUTES` — path prefix → generic CRUD handlers from
 *      `createMockHandlers`, for entity collections.
 *
 * Paths carry their version and actor segments and come from `API_ROUTES`.
 * Mock data lives in `./data/` and follows the `@shared/contracts` response
 * contracts, so screens cannot tell mock from real. Anything unmatched still
 * throws `Mock API: No handler for <url>` — add a route rather than a fallback.
 *
 * Note this client returns the **normalized** envelope, not Laravel's wire
 * shape: it stands in for the real client, not for the server.
 */

import {
  API_ROUTES,
  type ApiResponse,
  type PaginatedQueryParams,
  type PaginatedResponse,
  type PaginationType,
} from "@shared/contracts";
import type { HttpClient, QueryParams } from "../interfaces";
import { createMockHandlers } from "./mock-helpers";
import {
  mockCurrentUser,
  mockGames,
  mockLogin,
  mockRegister,
  mockSession,
} from "./data";

// Simulate network delay
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type MockHandlers = ReturnType<typeof createMockHandlers>;
type MockMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface MockRequest {
  url: string;
  body?: unknown;
  params?: Record<string, unknown>;
  /** Capture groups when the route was declared with a regex. */
  match: string[];
}

interface MockRoute {
  method: MockMethod;
  path: string | RegExp;
  handle: (request: MockRequest) => unknown;
  /** The envelope message, as the backend's localized `trans('site.*')` would be. */
  message?: string;
}

/**
 * Non-CRUD endpoints. Declared with the generated route constants where the
 * path is fixed, and with an anchored regex where it carries an id.
 */
const CUSTOM_ROUTES: MockRoute[] = [
  {
    method: "POST",
    path: API_ROUTES.AUTH.login(),
    // Returns null for the password "wrong", mirroring the backend's
    // HTTP-200-with-empty-data credential failure.
    handle: ({ body }) => mockLogin(body),
  },
  {
    method: "POST",
    path: API_ROUTES.AUTH.register(),
    handle: ({ body }) => mockRegister(body),
  },
  {
    method: "GET",
    path: API_ROUTES.AUTH.me(),
    // Flat, like the real endpoint — not wrapped in `{ user }`.
    handle: () => ({ ...mockCurrentUser }),
  },
  {
    method: "PUT",
    path: API_ROUTES.AUTH.me(),
    handle: ({ body }) => mockRegister(body),
  },
  {
    method: "POST",
    path: API_ROUTES.AUTH.refresh(),
    handle: () => mockSession({ ...mockCurrentUser }),
  },
  {
    // GET, matching routes/v1/api/admin.php.
    method: "GET",
    path: API_ROUTES.AUTH.logout(),
    handle: () => null,
    message: "Logout successfully",
  },
  {
    method: "POST",
    path: API_ROUTES.AUTH.requestResetPassword(),
    handle: () => null,
    message: "Password reset code sent",
  },
  {
    method: "POST",
    path: API_ROUTES.AUTH.validateResetPasswordCode(),
    handle: () => null,
    message: "Code correct",
  },
  {
    method: "POST",
    path: API_ROUTES.AUTH.resetPassword(),
    handle: () => null,
    message: "Password reset successfully",
  },
];

/**
 * Path prefix → generic CRUD handlers. Custom routes above win, so a prefix
 * here never has to worry about its own sub-resources.
 */
const HANDLER_ROUTES: Record<string, MockHandlers> = {
  [API_ROUTES.GAME.base]: createMockHandlers(mockGames, {
    searchFields: ["name", "slug"],
  }),
};

/**
 * Wrap a payload in the normalized envelope, so mock and real responses are
 * indistinguishable to callers. The status is always 200 — this backend never
 * returns 201, not even for a create.
 */
function envelope<T>(data: T, message = ""): ApiResponse<T> {
  return { statusCode: 200, message, data };
}

/**
 * FormData → a plain object the generic handlers can merge.
 *
 * `File` entries become a media stub so a mocked game still renders an image,
 * and `_method` is dropped because it is transport plumbing, not a field.
 */
function formDataToObject(formData: FormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of formData.entries()) {
    if (key === "_method") continue;

    result[key] =
      value instanceof File
        ? {
            url: `/storage/mock/${value.name}`,
            size: Math.round(value.size / 1024),
            extension: value.name.split(".").pop() ?? "",
            mime_type: value.type,
          }
        : value;
  }

  return result;
}

function stripQuery(url: string): string {
  const index = url.indexOf("?");
  return index === -1 ? url : url.slice(0, index);
}

class MockApiClient implements HttpClient {
  private async simulateRequest<T>(data: T): Promise<T> {
    await delay(300); // Simulate network delay
    return data;
  }

  /** First custom route whose method and path match, with regex captures. */
  private matchCustom(
    method: MockMethod,
    url: string
  ): { route: MockRoute; match: string[] } | null {
    const path = stripQuery(url);
    for (const route of CUSTOM_ROUTES) {
      if (route.method !== method) continue;
      if (typeof route.path === "string") {
        if (route.path === path) return { route, match: [path] };
        continue;
      }
      const found = path.match(route.path);
      if (found) return { route, match: Array.from(found) };
    }
    return null;
  }

  private async runCustom<T>(
    method: MockMethod,
    url: string,
    body?: unknown,
    params?: Record<string, unknown>
  ): Promise<ApiResponse<T> | null> {
    const hit = this.matchCustom(method, url);
    if (!hit) return null;
    const result = hit.route.handle({ url, body, params, match: hit.match });
    return this.simulateRequest(envelope(result as T, hit.route.message));
  }

  private matchRoute(url: string): { prefix: string; handlers: MockHandlers } {
    const path = stripQuery(url);
    const prefix = Object.keys(HANDLER_ROUTES).find((route) =>
      path.startsWith(route)
    );
    const handlers = prefix ? HANDLER_ROUTES[prefix] : undefined;
    if (!prefix || !handlers) {
      throw new Error(`Mock API: No handler for ${url}`);
    }
    return { prefix, handlers };
  }

  private requireHandlers(url: string): MockHandlers {
    return this.matchRoute(url).handlers;
  }

  private idFrom(url: string): string {
    const parts = stripQuery(url).split("/").filter(Boolean);
    return parts[parts.length - 1];
  }

  async get<T>(url: string, params?: QueryParams): Promise<ApiResponse<T>> {
    const custom = await this.runCustom<T>("GET", url, undefined, params);
    if (custom) return custom;

    const { prefix, handlers } = this.matchRoute(url);

    // Detail endpoint ("/v1/items/123") vs list endpoint ("/v1/items") —
    // compared against the registered prefix so version segments don't count.
    const segments = (path: string) => path.split("/").filter(Boolean).length;
    const isDetail = segments(stripQuery(url)) > segments(prefix);
    const result = isDetail
      ? handlers.get(this.idFrom(url))
      : handlers.getAll(params);

    return this.simulateRequest(envelope(result as T));
  }

  async getPaginated<T, TPaginationType extends PaginationType = "offset">(
    url: string,
    query?: PaginatedQueryParams
  ): Promise<ApiResponse<PaginatedResponse<T, TPaginationType>>> {
    const handlers = this.requireHandlers(url);
    const page = handlers.getPage(query) as PaginatedResponse<
      T,
      TPaginationType
    >;

    return this.simulateRequest(envelope(page));
  }

  async post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const custom = await this.runCustom<T>("POST", url, data);
    if (custom) return custom;

    const handlers = this.requireHandlers(url);

    // A multipart create/update arrives as FormData; the generic handlers work
    // on plain objects, so flatten it. `_method=PUT` means this POST is really
    // an update of `/:id` (see `buildGameFormData`).
    const payload = data instanceof FormData ? formDataToObject(data) : data;
    if (data instanceof FormData && data.get("_method") === "PUT") {
      return this.simulateRequest(
        envelope(handlers.update(this.idFrom(url), payload) as T)
      );
    }

    return this.simulateRequest(envelope(handlers.create(payload) as T));
  }

  async upload<T>(url: string, formData: FormData): Promise<ApiResponse<T>> {
    return this.post<T>(url, formData);
  }

  async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const custom = await this.runCustom<T>("PUT", url, data);
    if (custom) return custom;
    return this.patch<T>(url, data);
  }

  async patch<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const custom = await this.runCustom<T>("PATCH", url, data);
    if (custom) return custom;

    const handlers = this.requireHandlers(url);
    const result = handlers.update(this.idFrom(url), data);
    return this.simulateRequest(envelope(result as T));
  }

  async delete<T = null>(url: string): Promise<ApiResponse<T>> {
    const custom = await this.runCustom<T>("DELETE", url);
    if (custom) return custom;

    const handlers = this.requireHandlers(url);
    handlers.delete(this.idFrom(url));
    return this.simulateRequest(envelope(null as T));
  }
}

export const mockApi = new MockApiClient();
